
import { useState } from 'react';

interface CaseResult {
  metric: string;
  before: string;
  after: string;
  growth: string;
}

interface CaseStudy {
  id: number;
  company: string;
  category: string;
  owner: string;
  role: string;
  image: string;
  ownerImage: string;
  logo: string;
  testimonial: string;
  challenge: string;
  solution: string;
  results: CaseResult[];
  timeline: string;
  mainMetric: string;
  mainMetricLabel: string;
}

interface CaseCardProps {
  caseStudy: CaseStudy;
  index: number;
}

export default function CaseCard({ caseStudy, index }: CaseCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div 
      className="group bg-gradient-to-b from-white/5 to-transparent border border-white/10 rounded-2xl overflow-hidden hover:border-[#10B981]/50 transition-all duration-500"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Imagem */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={caseStudy.image} 
          alt={caseStudy.company}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        
        {/* Badge de categoria */}
        <div className="absolute top-4 left-4">
          <span className="bg-[#10B981] text-black text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
            {caseStudy.category}
          </span>
        </div>
        
        {/* Métrica principal */}
        <div className="absolute bottom-4 right-4 bg-black/80 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-2">
          <p className="text-[#10B981] text-2xl font-bold">{caseStudy.mainMetric}</p>
          <p className="text-white/70 text-xs whitespace-nowrap">{caseStudy.mainMetricLabel}</p>
        </div>
        
        {/* Logo da empresa */}
        <div className="absolute bottom-4 left-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
            <span className="text-black font-bold text-lg">{caseStudy.logo}</span>
          </div>
          <div>
            <p className="text-white font-bold text-sm">{caseStudy.company}</p>
            <p className="text-white/60 text-xs whitespace-nowrap">{caseStudy.timeline} de implementação</p>
          </div>
        </div>
      </div>
      
      {/* Conteúdo */}
      <div className="p-6">
        {/* Depoimento */}
        <div className="mb-6">
          <i className="ri-double-quotes-l text-[#10B981]/30 text-3xl"></i>
          <p className="text-white/80 text-sm leading-relaxed line-clamp-3">
            {caseStudy.testimonial}
          </p>
        </div>
        
        {/* Autor */}
        <div className="flex items-center gap-3 mb-6 pb-6 border-b border-white/10">
          <img 
            src={caseStudy.ownerImage} 
            alt={caseStudy.owner}
            className="w-12 h-12 rounded-full object-cover object-top"
          />
          <div>
            <p className="text-white font-medium text-sm">{caseStudy.owner}</p>
            <p className="text-white/60 text-xs">{caseStudy.role}</p>
          </div>
        </div>
        
        {/* Resultados resumidos */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {caseStudy.results.slice(0, 2).map((result, idx) => (
            <div key={idx} className="bg-white/5 rounded-xl p-3">
              <p className="text-white/60 text-xs mb-1 whitespace-nowrap">{result.metric}</p>
              <div className="flex items-center gap-2">
                <span className="text-white/40 text-sm line-through">{result.before}</span>
                <i className="ri-arrow-right-line text-[#10B981] text-xs"></i>
                <span className="text-white font-bold text-sm">{result.after}</span>
              </div>
              <span className="text-[#10B981] text-xs font-medium">{result.growth}</span>
            </div>
          ))}
        </div>
        
        {/* Botão expandir */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-[#10B981]/20 border border-white/10 hover:border-[#10B981]/50 rounded-xl py-3 transition-all cursor-pointer"
        >
          <span className="text-white text-sm font-medium whitespace-nowrap">
            {isExpanded ? 'Ver menos' : 'Ver caso completo'}
          </span>
          <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-[#10B981]`}></i>
        </button>
        
        {/* Conteúdo expandido */}
        {isExpanded && (
          <div className="mt-6 pt-6 border-t border-white/10 animate-fadeIn">
            {/* Desafio */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-red-500/20 rounded-lg">
                  <i className="ri-error-warning-line text-red-400"></i>
                </div>
                <h4 className="text-white font-bold text-sm whitespace-nowrap">O Desafio</h4>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">{caseStudy.challenge}</p>
            </div>
            
            {/* Solução */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#10B981]/20 rounded-lg">
                  <i className="ri-lightbulb-line text-[#10B981]"></i>
                </div>
                <h4 className="text-white font-bold text-sm whitespace-nowrap">A Solução</h4>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">{caseStudy.solution}</p>
            </div>
            
            {/* Todos os resultados */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#10B981]/20 rounded-lg">
                  <i className="ri-bar-chart-box-line text-[#10B981]"></i>
                </div>
                <h4 className="text-white font-bold text-sm whitespace-nowrap">Resultados Detalhados</h4>
              </div>
              <div className="space-y-3">
                {caseStudy.results.map((result, idx) => (
                  <div key={idx} className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white/80 text-sm font-medium">{result.metric}</span>
                      <span className="text-[#10B981] font-bold">{result.growth}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <p className="text-white/40 text-xs mb-1 whitespace-nowrap">Antes</p>
                        <p className="text-white/60 text-sm">{result.before}</p>
                      </div>
                      <i className="ri-arrow-right-line text-[#10B981]"></i>
                      <div className="flex-1">
                        <p className="text-[#10B981]/60 text-xs mb-1 whitespace-nowrap">Depois</p>
                        <p className="text-white font-bold text-sm">{result.after}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
